import React from "react";
import { motion } from "framer-motion";
import {
  UserPlus,
  CreditCard,
  MessageCircle,
  BadgeCheck,
  Building2,
} from "lucide-react";
import { LANDING } from "@/constants/testIds";

const STEPS = [
  {
    icon: UserPlus,
    title: "Add the recipient",
    desc: "Payer adds the educator's or professional's bank account, PAN and mobile number — saved securely for next time.",
  },
  {
    icon: CreditCard,
    title: "Make the payment",
    desc: "Choose your method — Credit Card, Prepaid Card, Wallet, EMI or UPI. Pay the full amount upfront.",
  },
  {
    icon: MessageCircle,
    title: "Seller gets an invite",
    desc: "EMDpay sends the educator or professional an onboarding invite over WhatsApp and SMS.",
  },
  {
    icon: BadgeCheck,
    title: "Seller completes KYC",
    desc: "The recipient completes a quick KYC and onboards as a verified seller on the EMDpay marketplace.",
  },
  {
    icon: Building2,
    title: "Money settles to the seller",
    desc: "Funds are released directly to the seller's bank account once KYC is verified — clean, traceable, regulated.",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how"
      data-testid={LANDING.howItWorks}
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-end justify-between gap-8 flex-wrap mb-14">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F25C2A]">
              · How the marketplace works
            </p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#0A0A0A]">
              From payer to <em className="italic text-[#F25C2A] font-normal">verified seller</em>.
              In five calm steps.
            </h2>
          </div>
          <p className="max-w-md text-[#4A4A4A] leading-relaxed">
            EMDpay is a marketplace — payers transact, and educators &amp;
            professionals onboard as KYC-verified sellers. Funds settle only
            after verification.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="group relative bg-white border border-[#E5E5E0] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(0,0,0,0.1)]"
            >
              <div className="absolute top-6 right-6 font-serif text-5xl text-[#FEF0EB] leading-none">
                0{i + 1}
              </div>
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FEF0EB] text-[#F25C2A] group-hover:bg-[#F25C2A] group-hover:text-white transition-colors duration-300">
                <step.icon size={22} />
              </span>
              <h3 className="mt-5 font-serif text-xl font-medium leading-snug text-[#0A0A0A]">
                {step.title}
              </h3>
              <p className="mt-2 text-sm text-[#4A4A4A] leading-relaxed">
                {step.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
